import { useMemo } from 'react'
import { unique } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { resolveSearchMapDisplayOffer } from './utils'

/**
 * Resolves offers for map pins.
 *
 * Control (`enableStreamMetaPins` false): current page + selected only.
 * Treatment: selected → mapOfferIds → current page → remaining list; optional markerCap.
 */
export default function useSearchMapResults(
  selectedOfferIds: Array<string>,
  overrideOfferList?: App.OfferList,
  pageSize: number = 20,
  offerList?: App.OfferList,
  filters: App.OfferListFilters = {},
  enableStreamMetaPins: boolean = false,
  markerCap?: number,
) {
  const offerMetaDataKey = getOfferListKey(filters)
  const streamMetaByOfferId = useAppSelector(
    state => state.offer.searchResultMetadata.offerMetaData[offerMetaDataKey],
  )

  const offerIds = useMemo(() => {
    if (!enableStreamMetaPins) {
      if (!offerList?.fetching && overrideOfferList) {
        return unique([...overrideOfferList.offerIds, ...selectedOfferIds])
      }

      return []
    }

    const orderedIds = unique([
      ...selectedOfferIds,
      ...(offerList?.mapOfferIds ?? []),
      ...(overrideOfferList?.offerIds ?? []),
      ...(offerList?.offerIds ?? []),
    ])

    return markerCap ? orderedIds.slice(0, markerCap) : orderedIds
  }, [
    enableStreamMetaPins,
    markerCap,
    offerList?.fetching,
    offerList?.mapOfferIds,
    offerList?.offerIds,
    overrideOfferList,
    selectedOfferIds,
  ])

  const [loadedOffers, offersFetching] = useOffers<App.HotelOffer | App.BedbankOffer>(offerIds)

  const offers = useMemo(() => {
    const loadedOfferMap = new Map(loadedOffers.map(offer => [offer.id, offer]))

    if (!enableStreamMetaPins) {
      return offerIds.map(id => loadedOfferMap.get(id)).filter(excludeNullOrUndefined)
    }

    return offerIds.map(offerId => resolveSearchMapDisplayOffer(
      loadedOfferMap.get(offerId),
      streamMetaByOfferId?.[offerId],
    )).filter(excludeNullOrUndefined)
  }, [enableStreamMetaPins, loadedOffers, offerIds, streamMetaByOfferId])

  const totalNumOffers = overrideOfferList?.offerCount || 0
  const paginatedOfferCount = overrideOfferList?.offerIds?.length ?? 0

  const fetchingList = (offerList?.offerIds?.length || 0) < totalNumOffers
  // Control: page is still loading while the page window is incomplete and the stream continues.
  // Treatment: also require offerList.fetching so a long unfinished stream does not stick the
  // "Loading offers" banner after markers are already shown (fetching clears on list flushes).
  const fetchingPage = paginatedOfferCount < pageSize &&
    fetchingList &&
    !overrideOfferList?.streamEnded &&
    (!enableStreamMetaPins || !!offerList?.fetching)
  const error = overrideOfferList?.error && !overrideOfferList?.streamEnded
  const offersReadyForPins = enableStreamMetaPins && offerIds.length > 0 && offers.length === offerIds.length
  const streamMetaOffersLoaded = offerIds.length === 0 ||
    (offersReadyForPins && !offersFetching && loadedOffers.length === offerIds.length)
  const allOffersLoaded = enableStreamMetaPins
    ? !fetchingList && streamMetaOffersLoaded
    : !fetchingList && !offersFetching && offers.length === totalNumOffers
  const noOffersAvailable = !fetchingList && totalNumOffers === 0
  const key = overrideOfferList?.key
  const offersInteractable = enableStreamMetaPins ? offersReadyForPins : allOffersLoaded

  return useMemo(() => ({
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
  }), [
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    offersReadyForPins,
    offersInteractable,
    noOffersAvailable,
    key,
    fetchingPage,
  ])
}
